import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkButtonVariant,
  Heading,
  LinkButton,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import querystring from 'query-string';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import SimilarRentalsCarousel from '@/components/common/SimilarRentalsCarousel';
import { EHeaderTab } from '@/components/ui/SearchHeader/UniversalSearch/UniversalSearchContainer/menu';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { addFilters, applyFilters, goToRentals, resetFilters } from '@/redux/actions/rentals';
import { applySearchFilter } from '@/redux/modules/search';
import { getCurrentFilters, getRentalsFilters } from '@/redux/selectors/listing/rentalsFilters';
import { getSimilarRentalsTiles } from '@/redux/selectors/listing/similarRentals';
import { EListingSource } from '@/services/analytics/listings/types';
import { trackRenterSearchedEvent } from '@/services/analytics/search';
import { ERentalType } from '@/services/analytics/types';
import { useSerpLocalitySearchDecision } from '@/services/experiments/serpLocalitySearchResults';
import { LocationGroupType } from '@/utility/surfacedLocation';

import NoResultsSection from './NoResultsSection';

interface ICarouselProps {
  title: string;
  rvType: string;
  serpGuidedSearchEnabled?: boolean;
  address: string;
  bounds?: Record<string, string>;
  localitySearchFilters?: Record<string, string>;
}

const ExperimentalCarousel: React.FC<ICarouselProps> = ({
  title,
  rvType,
  serpGuidedSearchEnabled,
  address,
  bounds,
  localitySearchFilters,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const rentalsResultCount = useSelector(getSimilarRentalsTiles).length;
  const { dateFrom, dateTo } = useSelector(getRentalsFilters);
  const currentFilters = useSelector(getCurrentFilters);
  const { isMobile } = useBreakpoint();
  const shouldRedirectLocalitySearch = !useSerpLocalitySearchDecision().enabled;

  const isPetFriendly = (() => {
    try {
      return currentFilters?.[ESearchFilters.FILTER_FEATURE]?.includes('pet_friendly');
    } catch {
      return false;
    }
  })();

  const noResultsMessage = intl.formatMessage({
    defaultMessage: 'Sorry, no rentals matched the filters you selected',
    id: 'uA7DX6',
  });
  const noResultsSearchMoreRvsMessage = intl.formatMessage(
    {
      defaultMessage: `Search for more {rvType}s`,
      id: 'mw7Aj5',
    },
    { rvType },
  );
  const searchMoreRvsMessage = serpGuidedSearchEnabled
    ? intl.formatMessage({
        defaultMessage: 'Search for more listings',
        id: 'Jx2lbz',
      })
    : intl.formatMessage(
        {
          defaultMessage: 'View more {locality} rentals',
          id: 'QyvhFd',
        },
        { locality: title },
      );

  const onSeeMoreRentals = async () => {
    await trackRenterSearchedEvent({
      surfacedLocations: LocationGroupType.OTHER,
      totalAdults: null,
      totalChildren: null,
      totalInfants: null,
      locationQuery: null,
      source: 'serp_search',
      isADAAccessible: false,
      isPetFriendly,
      flexibleDateRange: null,
      departureDate: null,
      returnDate: null,
      hasCategories: false,
      selectedCategory: EHeaderTab.ALL,
      towableYear: null,
      towableMake: null,
      towableModel: null,
      rentalType: ERentalType.RV,
      searchFilterCategory: null,
      lastQuestionSeen: null,
    });

    dispatch(
      applySearchFilter(
        {
          [ESearchFilters.ADDRESS]: address,
          ...(bounds || {}),
          ...(localitySearchFilters || {}),
        },
        false,
        false,
        true,
        undefined,
        false,
        shouldRedirectLocalitySearch,
      ),
    );
  };

  const onNoResultClick = () => {
    if (!rentalsResultCount) {
      dispatch(resetFilters());
      dispatch(
        addFilters({
          'filter[vehicle_model]': null,
          'filter[vehicle_make]': null,
          'filter[keywords]': null,
        }),
      );
      dispatch(applyFilters());
      dispatch(goToRentals());
    }
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-col mt-4 mb-2 lg:mt-6 lg:mb-6 lg:flex-row gap-4 lg:gap-0',
          serpGuidedSearchEnabled ? 'justify-between' : 'justify-center',
        )}>
        <Heading variant={EHeadingStyleVariant.H4} component="h2" className="text-center">
          <FormattedMessage
            defaultMessage="Explore {title} {rvType} rentals"
            id="Yd9a1j"
            values={{ title, rvType }}
          />
        </Heading>
        {serpGuidedSearchEnabled && (
          <LinkButton
            variant={ELinkButtonVariant.UnderlineWithIcon}
            label={searchMoreRvsMessage}
            onClick={onSeeMoreRentals}
          />
        )}
      </div>

      <SimilarRentalsCarousel
        extraQueryParams={
          dateFrom && dateTo ? querystring.stringify({ from: dateFrom, to: dateTo }) : ''
        }
        eventSource={EListingSource.EXPLORE_THIS_VEHICLE}
        verticalScrollGrid
        maxCols={1}
        isRow={isMobile}
      />

      <div className="w-full text-center">
        {(() => {
          if (serpGuidedSearchEnabled) return null;

          return rentalsResultCount ? (
            <Button
              className="w-full md:w-auto"
              variant={EButtonColorVariant.Primary}
              label={searchMoreRvsMessage}
              onClick={onSeeMoreRentals}
            />
          ) : (
            <NoResultsSection message={noResultsMessage}>
              <Button
                variant={EButtonColorVariant.Primary}
                label={noResultsSearchMoreRvsMessage}
                onClick={onNoResultClick}
              />
            </NoResultsSection>
          );
        })()}
      </div>
    </>
  );
};

export default ExperimentalCarousel;
